import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { useSubscription } from "@/contexts/SubscriptionContext";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Loader2, AlertCircle } from "lucide-react";
import { meta } from "@/lib/metaPixel";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { checkStatus } = useSubscription();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "polling", attempts: 0, data: null });
  const nav = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", attempts: 0, data: null });
      return;
    }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      try {
        const data = await checkStatus(sessionId);
        if (cancelled) return;
        attempts += 1;
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", attempts, data });
          // Meta: PURCHASE — the single most important conversion event. It
          // fires only after the payment provider confirms `payment_status:
          // "paid"`, and is deduped by Stripe session_id in localStorage so
          // page refreshes, back/forward navigation, browser restarts and
          // React re-renders cannot double-count. Value/currency/plan are
          // sourced from the real subscription payload returned by the API.
          const sub = data.subscription || {};
          meta.purchase({
            sessionId,
            planName: sub.tier || "unknown",
            // Backend returns `amount` in whole units (EUR) from payment_transactions.
            value: typeof data.amount === "number" ? data.amount : undefined,
            currency: (data.currency || sub.currency || "EUR").toUpperCase(),
          });
          return;
        }
        if (data.status === "expired") {
          setState({ status: "expired", attempts, data });
          return;
        }
        if (attempts >= MAX_ATTEMPTS) {
          setState({ status: "timeout", attempts, data });
          return;
        }
        setState({ status: "polling", attempts, data });
        setTimeout(poll, POLL_INTERVAL);
      } catch {
        if (!cancelled) setState((s) => ({ ...s, status: "error" }));
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, checkStatus, refresh]);

  return (
    <div data-testid="billing-success-page" className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-4">
      <div className="dms-card p-10 max-w-md w-full text-center">
        {state.status === "polling" && (
          <>
            <Loader2 className="w-10 h-10 text-[#1D4E89] animate-spin mx-auto mb-5" />
            <h1 className="font-heading font-bold text-2xl text-[#0A2342]">Confirming payment…</h1>
            <p className="mt-3 text-[#475569]">This usually takes a few seconds.</p>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-14 h-14 rounded-full bg-[#4FD1C5]/15 flex items-center justify-center mx-auto mb-5">
              <Check className="w-7 h-7 text-[#1D4E89]" strokeWidth={2.5} />
            </div>
            <div className="dms-label mb-2 justify-center">Welcome aboard</div>
            <h1 className="font-heading font-bold text-2xl text-[#0A2342]">You&apos;re {state.data?.subscription?.tier === "pro" ? "Pro" : "Basic"}.</h1>
            <p className="mt-3 text-[#475569]">All your tools have unlocked. Go make something good.</p>
            <button data-testid="billing-success-cta" onClick={() => nav("/dashboard")} className="mt-7 dms-btn-primary w-full">Open dashboard</button>
          </>
        )}
        {(state.status === "expired" || state.status === "timeout" || state.status === "error") && (
          <>
            <AlertCircle className="w-10 h-10 text-[#1D4E89] mx-auto mb-5" />
            <h1 className="font-heading font-bold text-2xl text-[#0A2342]">We couldn&apos;t confirm payment</h1>
            <p className="mt-3 text-[#475569]">If you completed checkout, your plan will activate within a minute. Otherwise, try again from Settings.</p>
            <Link to="/settings" data-testid="billing-back" className="mt-7 dms-btn-ghost w-full">Back to Settings</Link>
          </>
        )}
      </div>
    </div>
  );
}
